import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { when } from '../lib/labels';
import { ActionButton, ErrorBox, Load, Section, Table, useApi } from '../ui/kit';
import { GMAIL_EXTENSION_URL } from '../../../src/ext/store';

// F26 FR12/FR13: the Gmail extension in the app. Each person's own preferences, the workspace switches (owners and
// admins), the signed-in browsers with Sign out, and how to install it until the Chrome Web Store listing is live.

type Prefs = { meeting_link: string | null; draft_length: 'short' | 'medium'; tone: 'plain' | 'warm' | 'formal'; guard: boolean; chips: boolean; extra_accounts: string[] };
type Switches = { enabled: boolean; guard_forced: boolean; chips_enabled: boolean };
type ExtSettings = { workspace: Switches; me: Prefs };
type ExtSession = { id: string; email: string | null; mine: boolean; mode: string; browser: string | null; version: string | null; created_at: string; last_used_at: string | null; expires_at: string };

const browserName = (ua: string | null) => (!ua ? 'Chrome' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : ua.slice(0, 40));

export function GmailExtension({ admin }: { admin: boolean }) {
  const q = useApi<ExtSettings>('/portal/ext/settings');
  return (
    <>
      <Install />
      {q.isLoading ? <p className="muted" role="status">Loading…</p> : q.error ? <ErrorBox error={q.error} retry={() => q.refetch()} /> : q.data ? <>
        {!q.data.workspace.enabled ? <div className="banner" role="status">The Gmail extension is turned off for this workspace{admin ? '. Turn it on below.' : '. An owner or admin can turn it on.'}</div> : null}
        <MyPrefs s={q.data} onSaved={() => void q.refetch()} />
        {admin ? <WorkspaceSwitches s={q.data.workspace} onSaved={() => void q.refetch()} /> : null}
      </> : null}
      <Sessions admin={admin} />
    </>
  );
}

// unlisted on the Chrome Web Store: reachable by this link only, not by search
const STORE_URL = GMAIL_EXTENSION_URL;
const STORE_ID = STORE_URL.split('/').pop();

function Install() {
  return (
    <Section title="Install Outlevo for Gmail">
      <p className="small">A sidebar in Gmail: who wrote, why their company fits, what to do next, with sources. Outlevo never sends from Gmail; you always press Send.</p>
      <p className="small muted">Install it from the Chrome Web Store: <a href={STORE_URL} target="_blank" rel="noreferrer">Outlevo for Gmail</a> → Add to Chrome. Then open Gmail and press <kbd>Alt</kbd>+<kbd>O</kbd>, or the Outlevo tab on the right edge, and sign in.</p>
      <p className="small muted">Google Workspace admins can force-install it for everyone: Admin console → Devices → Chrome → Apps &amp; extensions, add the extension by ID <code>{STORE_ID}</code>.</p>
    </Section>
  );
}

function MyPrefs({ s, onSaved }: { s: ExtSettings; onSaved: () => void }) {
  const [v, setV] = useState<Prefs>(s.me);
  const [accounts, setAccounts] = useState(s.me.extra_accounts.join('\n'));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { setV(s.me); setAccounts(s.me.extra_accounts.join('\n')); }, [s]);
  const set = <K extends keyof Prefs>(k: K, x: Prefs[K]) => { setV({ ...v, [k]: x }); setSaved(false); };
  const save = async () => {
    setBusy(true); setErr(null);
    // a switch the workspace decides is not sent: it would not change anything
    const me: Record<string, unknown> = { meeting_link: v.meeting_link?.trim() || null, draft_length: v.draft_length, tone: v.tone, extra_accounts: accounts.split(/[\s,]+/).map((a) => a.trim()).filter(Boolean) };
    if (!s.workspace.guard_forced) me.guard = v.guard;
    if (s.workspace.chips_enabled) me.chips = v.chips;
    try { await api('/portal/ext/settings', { method: 'PUT', body: { me } }); setSaved(true); onSaved(); } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  return (
    <Section title="Your Gmail extension">
      <div className="form">
        <label className="field">Meeting link (“Book a call” inserts it into your reply)
          <input id="gx-field-1" type="url" placeholder="https://calendly.com/you/intro" value={v.meeting_link ?? ''} disabled={busy} onChange={(e) => set('meeting_link', e.target.value)} />
        </label>
        <label className="field">Draft length
          <select id="gx-field-2" value={v.draft_length} disabled={busy} onChange={(e) => set('draft_length', e.target.value as Prefs['draft_length'])}>
            <option value="short">Short (40 to 80 words)</option><option value="medium">Medium (90 to 140 words)</option>
          </select>
        </label>
        <label className="field">Tone
          <select id="gx-field-3" value={v.tone} disabled={busy} onChange={(e) => set('tone', e.target.value as Prefs['tone'])}>
            <option value="plain">Plain</option><option value="warm">Warm</option><option value="formal">Formal</option>
          </select>
        </label>
        <label className="field field-check"><input id="gx-field-4" type="checkbox" checked={s.workspace.guard_forced || v.guard} disabled={busy || s.workspace.guard_forced} onChange={(e) => set('guard', e.target.checked)} />
          <span>Recipient guard: warn before I write to someone who opted out or is not eligible{s.workspace.guard_forced ? ' (always on in this workspace)' : ''}</span></label>
        <label className="field field-check"><input id="gx-field-5" type="checkbox" checked={s.workspace.chips_enabled && v.chips} disabled={busy || !s.workspace.chips_enabled} onChange={(e) => set('chips', e.target.checked)} />
          <span>Inbox chips: “Interested”, “Hot lead” and the campaign beside known senders{s.workspace.chips_enabled ? '' : ' (off in this workspace)'}</span></label>
        <label className="field">Other Gmail accounts it may run in, one per line
          <textarea id="gx-field-6" rows={3} value={accounts} disabled={busy} placeholder="me@personal-domain.com" onChange={(e) => { setAccounts(e.target.value); setSaved(false); }} />
          <span className="muted small">It already runs in addresses on your workspace's domains and its sending mailboxes.</span>
        </label>
        <div className="form-actions"><button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save my settings'}</button></div>
        {saved ? <p className="small" role="status">Saved. The extension picks it up within 5 minutes.</p> : null}
        {err ? <ErrorBox error={err} doing="save your settings" /> : null}
      </div>
    </Section>
  );
}

function WorkspaceSwitches({ s, onSaved }: { s: Switches; onSaved: () => void }) {
  const [v, setV] = useState<Switches>(s);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => setV(s), [s]);
  const save = async () => {
    if (s.enabled && !v.enabled && !confirm('Turn the Gmail extension off for everyone? Every signed-in browser is signed out at once.')) return;
    setBusy(true); setErr(null);
    try { await api('/portal/ext/settings', { method: 'PUT', body: { workspace: v } }); onSaved(); } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const changed = v.enabled !== s.enabled || v.guard_forced !== s.guard_forced || v.chips_enabled !== s.chips_enabled;
  return (
    <Section title="For the whole workspace">
      <div className="form">
        <label className="field field-check"><input id="gx-field-7" type="checkbox" checked={v.enabled} disabled={busy} onChange={(e) => setV({ ...v, enabled: e.target.checked })} />
          <span>The Gmail extension is on for members (off signs everyone out and refuses sign-in)</span></label>
        <label className="field field-check"><input id="gx-field-8" type="checkbox" checked={v.guard_forced} disabled={busy} onChange={(e) => setV({ ...v, guard_forced: e.target.checked })} />
          <span>Recipient guard always on: nobody can turn the opt-out warning off</span></label>
        <label className="field field-check"><input id="gx-field-9" type="checkbox" checked={v.chips_enabled} disabled={busy} onChange={(e) => setV({ ...v, chips_enabled: e.target.checked })} />
          <span>Inbox chips allowed</span></label>
        <div className="form-actions"><button type="button" className="btn btn-primary" disabled={busy || !changed} onClick={() => void save()}>{busy ? 'Saving…' : 'Save workspace settings'}</button></div>
        {err ? <ErrorBox error={err} doing="save the workspace settings" /> : null}
      </div>
    </Section>
  );
}

function Sessions({ admin }: { admin: boolean }) {
  return (
    <Load<{ data: ExtSession[] }> path="/portal/ext/sessions">{(d) => (
      <Section title={admin ? 'Signed-in browsers in this workspace' : 'Your signed-in browsers'}>
        <Table rows={d.data} columns={[
          ...(admin ? [{ key: 'email', label: 'Person', render: (r: ExtSession) => (r.mine ? 'You' : r.email ?? 'A member') }] : []),
          { key: 'browser', label: 'Browser', render: (r) => `${browserName(r.browser)}${r.version ? ` · extension ${r.version}` : ''}` },
          { key: 'mode', label: 'Mode', render: (r) => (r.mode === 'test' ? 'Test (sandbox)' : 'Live') },
          { key: 'created_at', label: 'Signed in', render: (r) => when(r.created_at) },
          { key: 'last_used_at', label: 'Last used', render: (r) => (r.last_used_at ? when(r.last_used_at) : 'Not yet') },
          { key: 'id', label: '', render: (r) => <ActionButton path={`/portal/ext/sessions/${r.id}/revoke`} label="Sign out" variant="danger" confirm={r.mine ? 'Sign this browser out of Outlevo for Gmail?' : `Sign ${r.email ?? 'this person'} out of Outlevo for Gmail?`} /> },
        ]} empty="No browser is signed in to Outlevo for Gmail." />
      </Section>
    )}</Load>
  );
}
